import React from "react";
import growthMindset from "../../images/articles/powerOfMindset/growth-mindset.png";
import positiveThinking from "../../images/articles/powerOfMindset/positive-thinking.png";
import selfBelief from "../../images/articles/powerOfMindset/self-belief.png";
import "../../styles/blogs.css";

const powerOfMindset = () => {
  return (
    <>
      <section className="blog">
        <div className="blogIntro">
          <h1>
            The power of your mindset: Unlocking your true potential for success
          </h1>
          <p>
            Hopefully you had a chance to read our recent blog about how
            important it is to have the right mindset, skillset and toolset. If
            you haven’t, you can read it here. You might have thought about how
            you harness these, but have you considered how your mindset alone
            can hinder or excel your success? This can be both positive and
            negative.
          </p>
          <p>
            Your mindset plays a crucial role in determining success. Your
            mindset is your beliefs, attitudes, and thoughts, which influence
            how you perceive and respond to the challenges and opportunities
            that come your way. Success is a subjective concept that can have
            multiple connotations, ranging from professional achievements to
            personal goals. In this blog, we will explore the power of your
            mindset and how it can influence, or hinder, your journey towards
            success.
          </p>
        </div>
        <div className="blogPara">
          <h3>Growth Mindset</h3>
          <p>
            Having a positive and growth-oriented mindset is essential for
            achieving success. When you have a positive mindset, you believe in
            your abilities and act optimistic about your chances of success. A
            positive outlook helps you approach challenges as learning
            opportunities and maintain resilience in the face of setbacks.
            Similarly, a growth mindset is characterized by the belief that
            abilities and intelligence can be developed through effort and
            learning. With a growth mindset, you embrace challenges, persist in
            the face of obstacles, and view feedback and criticism as valuable
            tools for improvement.
          </p>
          <img src={growthMindset} alt="Growth Mindset" />
        </div>
        <div className="blogPara">
          <h3>
            Unhelpful, fixed and automatic negative thoughts (ANTs) mindset
          </h3>
          <p>
            On the other hand, an unhelpful or fixed mindset can hinder success.
            An unhelpful mindset is characterized by self-doubt, fear of
            failure, and limited belief in one's abilities. This can create a
            self-fulfilling prophecy, where your negative beliefs and attitudes
            become barriers to reaching your goals. A fixed mindset, which
            involves the belief that abilities and intelligence are fixed traits
            that cannot be changed, can also limit success. When you believe
            your abilities are fixed, you may shy away from challenges, fear
            failure, and avoid taking risks.
          </p>
          <p>
            We’re all human, so we will all have automatic unhelpful thoughts at
            some point. Some more often than others. Automatic Negative Thoughts
            (ANTs) are negative beliefs that occur habitually. They are
            involuntary responses to certain situations that are based on the
            core beliefs we hold about yourself, others, or the world.
          </p>
          <p>
            ANTs can lead to feelings of anger, self-doubt, depression, and
            anxiety. They have a strong influence on our mood and depending on
            if you choose to accept or ignore them, it will then have impact on
            your success. You cannot directly control ANTs, but you can control
            how you react following these thoughts.
          </p>
          <p>Examples of ANTs are:</p>
          <ul>
            <li>Always feeling negative</li>
            <li>Makes you feel bad about yourself</li>
            <li>Self-limiting</li>
            <li>
              Believable because you are stuck in an unhelpful thinking spiral
            </li>
            <li>Biased because you have a distorted perception of reality</li>
          </ul>
          <p>
            These types of thoughts are very normal. However, it’s how you deal
            with them that makes the difference in your success. 90% of all
            thoughts are repetitive, so if you repeat something enough, it
            creates a neural pathway in the brain. So, if your unhelpful
            thoughts get repeated enough, they become habitual. If not
            recognised and dealt with, these thoughts impact our resilience and
            leave us fragile. The worse thing about these thoughts is that a lot
            of the time they aren’t real or aren’t as bad as our brain makes out
            – they get distorted by our own lens.
          </p>
          <p>We’ve put together an example of this in action…</p>
          <div className="caseStudy">
            <h4>Meet Sarah</h4>
            <p>
              Sarah is about to do a presentation to the leadership team.
              Although she a subject matter expert, she can’t help but feel
              nervous. What if she forgets what she is going to say? What if
              someone disagrees with her? This leads to thoughts like:{" "}
              <span className="italic">
                “I’m not good at this. I’m rubbish and I shouldn’t be doing this
                job; everyone is so much better!”
              </span>
              .
            </p>
            <p>
              As Sarah goes into the meeting her legs are shaking, her breathing
              is shallow, and her hands tremble. She goes to introduce herself
              and stumbles over her name.
              <span className="italic">“Noooooo! What is wrong with me?”</span>.
              She looks at the faces staring back at her and says to herself
              <span className="italic">
                “They think I’m stupid. I bet they wonder why I’m even employed
                here.”
              </span>
            </p>
            <p>
              Sarah gets through the presentation at super speed, the main goal
              is to get out of that room!
            </p>
            <p>
              After the meeting Sarah feels frustrated and upset. She tells
              herself that she’s never going to be seen as someone that can
              progress and is going to get overlooked. These thoughts become a
              daily occurrence and her behaviours follow in line with the
              thoughts – she no longer puts herself forward for things as she is
              convinced that she will mess it up. As she has no evidence to
              prove her otherwise, because she’s not taking any opportunities,
              the negative thoughts continue.
            </p>
            <p>
              Sarah’s automatic unhelpful thoughts can be categorised into
              different thought distortions such as labelling, catastrophising
              and mind reading to name a few.
            </p>
          </div>
          <p>
            We see many people whose mindset holds them back from their true
            potential – sales people who struggle with challenging the status
            quo, learners with a fixed mindset and lots of other scenarios.
          </p>
          <p>So, how do we change these ANTs into positive ones?</p>
          <p className="strong">
            1. It is important to recognise and isolate the thought.
          </p>
          <ul>
            <li>Stop and pause!</li>
            <li>
              Pay attention to your body and emotions (emotional self-awareness)
            </li>
            <li>
              Isolate and focus on the unhelpful thought and separate it from
              who you are.
            </li>
          </ul>
          <p className="strong">2. Write the thought down.</p>
          <ul>
            <li>
              Think about what the thought is really about and write it down.
            </li>
          </ul>
          <p className="strong">
            3. Identify the distress level – on a scale of 0 = calm to 10 =
            extreme
          </p>
          <p className="strong">
            4. Next, try and identify what thought distortion it is. This helps
            you to recognise a pattern and, in the future, will help you to
            identify these thought distortions more quickly in the future.
          </p>
          <p className="strong">5. Challenge and reframe the thought.</p>
          <ul>
            <li>Look for evidence for and against the thought.</li>
            <li>Modify internal language.</li>
            <li>
              Replace negative distortions with positive healthy thoughts.
            </li>
            <li>Write down the more reasonable reframed thoughts.</li>
          </ul>
          <p className="strong">6. Re-evaluate the distress level.</p>
          <ul>
            <li>
              If the distress level has not decreased, consider repeating the
              exercise again.
            </li>
          </ul>
          <p>
            The skill of reality testing helps us to see things how they really
            are, not how they look through our own lens. This enables us to
            change thoughts that aren’t serving us well and making us feel
            distressed into thoughts that enable us to move forward.
          </p>
        </div>
        <div className="blogPara">
          <h3>The importance of positive thinking</h3>
          <p>
            Positive thinking is not about denying reality but rather
            approaching challenges with optimism and resilience. Positive
            thoughts can drive motivation, inspire creativity, and enhance
            problem-solving abilities. By focusing on possibilities rather than
            limitations, you can attract opportunities and maintain a sense of
            self-belief during difficult times. Cultivating a positive mindset
            can help you maintain focus, overcome obstacles, and persist in the
            face of setbacks, ultimately leading to success.
          </p>
          <img src={positiveThinking} alt="Positive Thinking" />
        </div>
        <div className="blogPara">
          <h3>The Importance of Self-Belief</h3>
          <p>
            One of the most significant factors influencing success is
            self-belief. If you do not believe in your abilities, it becomes
            challenging to achieve your goals. Your mindset shapes your beliefs
            about yourself, and a lack of confidence can hinder growth and limit
            your potential. Building self-belief involves acknowledging your
            strengths, celebrating your achievements, and setting realistic
            goals. By developing a strong sense of self-worth, you can overcome
            self-doubt and take confident steps towards success.
          </p>
          <img src={selfBelief} alt="Self Belief" />
        </div>
        <div className="blogPara">
          <h3>Embracing a Resilient Mindset</h3>
          <p>
            Resilience is the ability to bounce back from challenges and
            persevere in the face of adversity. A resilient mindset enables you
            to navigate uncertainty, embrace change, and adapt to unforeseen
            circumstances. It involves viewing setbacks as opportunities for
            growth and maintaining a positive outlook during difficult times. By
            developing resilience, you can overcome obstacles, learn from
            failures, and maintain the momentum needed for success.
          </p>
          <p>
            Your mindset is a powerful tool that can either propel you towards
            success or hold you back. By cultivating a growth mindset, embracing
            positive thinking, building self-belief, and developing resilience,
            you can unlock your potential and achieve your goals. Your mindset
            influences the way you approach challenges, learn from experiences,
            and ultimately grow as an individual. In summary, having a positive
            and growth-oriented mindset allows you to approach challenges with
            optimism, embrace growth and learning, and persevere in the face of
            setbacks. This mindset can significantly enhance your chances of
            achieving success in various areas of your life.
          </p>
          <p>
            At HiA, we work with a wide range of coaches that are certified in
            emotional intelligence, resilience, motivation mapping and lots of
            other areas that can help you with your mindset as well as your
            behaviour
          </p>
        </div>
      </section>
    </>
  );
};

export default powerOfMindset;
